import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { clearedReplacements, getBlackoutReplacements, replaceBlackoutResources } from "store/blackouts"
import { normalizeImpact } from "./normalizeImpact"

/** A stable empty fallback: a fresh `[]` from the selector would re-render on every action. */
const NO_ITEMS = []

/**
 * The Replace Crew/Equipment sheet's data and selection.
 *
 * Kept out of the component because the selection has one rule worth stating: an assignment is
 * only ever `bookingId -> candidateId`, so a guide can hand three stranded bookings to three
 * different people. Nothing is sent for a booking left unassigned — the server must not read a
 * missing key as "unassign", which is why the payload filters rather than sending nulls.
 */
export const useReplaceResources = ({ blackoutId, scope, isOpen }) => {
  const dispatch = useDispatch()
  const [assignments, setAssignments] = useState({})

  const replacements = useSelector((state) => state.blackouts?.replacements)
  const isSaving = useSelector((state) => Boolean(state.blackouts?.replaceResources?.loading))

  const isLoading = Boolean(replacements?.loading)
  const hasFailed = Boolean(replacements?.failed)

  const impact = useMemo(
    () => (replacements?.data ? normalizeImpact(replacements.data, scope) : null),
    [replacements?.data, scope]
  )
  const bookings = impact?.bookings || NO_ITEMS

  /**
   * Refetched on every open rather than cached: a candidate free ten minutes ago may have been
   * booked since, and offering a stale one would fail on submit with nothing on screen to explain it.
   */
  useEffect(() => {
    if (!isOpen || !blackoutId) return undefined
    setAssignments({})
    dispatch(getBlackoutReplacements(blackoutId)).catch(() => {})
    return () => dispatch(clearedReplacements())
  }, [dispatch, isOpen, blackoutId])

  const assignOne = useCallback((bookingId, candidateId) => {
    setAssignments((previous) => {
      const next = { ...previous }
      // antd clears a Select to `undefined`, which has to mean "leave this booking alone".
      if (candidateId === undefined || candidateId === null) delete next[bookingId]
      else next[bookingId] = candidateId
      return next
    })
  }, [])

  const assignedCount = Object.keys(assignments).length

  const submit = useCallback(() => {
    const payload = {
      assignments: bookings
        .filter((booking) => assignments[booking.id])
        .map((booking) => ({
          booking_id: booking.id,
          from_resource_id: booking.resource?.id,
          to_resource_id: assignments[booking.id]
        }))
    }
    return dispatch(replaceBlackoutResources(blackoutId, payload))
  }, [dispatch, blackoutId, bookings, assignments])

  return { bookings, isLoading, hasFailed, isSaving, assignments, assignedCount, assignOne, submit }
}
